import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';

interface MeetingType {
  id: 'physical' | 'virtual' | 'hybrid';
  name: string;
  icon: string;
  description: string;
}

interface Duration {
  value: string;
  label: string;
}

const CreateMeeting: React.FC = () => {
  const colors = getThemeColors('dark');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetingDate: '',
    meetingTime: '',
    duration: '60',
    location: '',
    meetingUrl: '',
    meetingType: 'physical' as 'physical' | 'virtual' | 'hybrid',
    attendeeEmails: '',
    addToCalendar: true,
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);

  const meetingTypes: MeetingType[] = [
    {
      id: 'physical',
      name: 'Physical Meeting',
      icon: 'location',
      description: 'In-person meeting at a location'
    },
    {
      id: 'virtual',
      name: 'Virtual Meeting',
      icon: 'videocam',
      description: 'Online meeting via video call'
    },
    {
      id: 'hybrid',
      name: 'Hybrid Meeting',
      icon: 'people',
      description: 'Both physical and virtual attendance'
    },
  ];

  const durations: Duration[] = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validate required fields
    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a meeting title';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Meeting title must be at least 3 characters long';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Meeting title must be less than 100 characters';
    }

    // Validate description length if provided
    if (formData.description.trim() && formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Validate meeting date
    if (!formData.meetingDate.trim()) {
      newErrors.meetingDate = 'Please select the meeting date';
    } else {
      try {
        const testDate = new Date(formData.meetingDate);
        if (isNaN(testDate.getTime())) {
          newErrors.meetingDate = 'Please enter a valid date';
        } else if (testDate < new Date(new Date().setHours(0, 0, 0, 0))) {
          newErrors.meetingDate = 'Meeting date must be today or in the future';
        }
      } catch (error) {
        newErrors.meetingDate = 'Please enter a valid date';
      }
    }

    // Validate meeting time
    if (!formData.meetingTime.trim()) {
      newErrors.meetingTime = 'Please select the meeting time';
    } else {
      // Validate time format (HH:MM)
      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(formData.meetingTime.trim())) {
        newErrors.meetingTime = 'Please enter a valid time in HH:MM format';
      }
    }

    // Validate combined date and time is in the future
    if (formData.meetingDate && formData.meetingTime && !newErrors.meetingDate && !newErrors.meetingTime) {
      try {
        const combinedDateTime = new Date(`${formData.meetingDate}T${formData.meetingTime}:00+03:00`);
        if (combinedDateTime < new Date()) {
          newErrors.meetingTime = 'Meeting time must be in the future';
        }
      } catch (error) {
        newErrors.meetingTime = 'Invalid date and time combination';
      }
    }

    // Validate location for physical meetings
    if (formData.meetingType === 'physical' && !formData.location.trim()) {
      newErrors.location = 'Please enter a location for physical meeting';
    }

    // Validate meeting URL for virtual meetings
    if ((formData.meetingType === 'virtual' || formData.meetingType === 'hybrid') && !formData.meetingUrl.trim()) {
      newErrors.meetingUrl = 'Please enter a meeting URL for virtual meeting';
    } else if ((formData.meetingType === 'virtual' || formData.meetingType === 'hybrid') && formData.meetingUrl.trim()) {
      // Basic URL validation
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(formData.meetingUrl.trim())) {
        newErrors.meetingUrl = 'Please enter a valid URL (e.g., https://zoom.us/j/123456789)';
      }
    }

    // Validate attendee emails if provided
    if (formData.attendeeEmails.trim()) {
      const emails = formData.attendeeEmails.split(',').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails: string[] = [];

      for (const email of emails) {
        if (email && !emailRegex.test(email)) {
          invalidEmails.push(email);
        }
      }

      if (invalidEmails.length > 0) {
        newErrors.attendeeEmails = `Invalid email format: ${invalidEmails.join(', ')}`;
      }
    }

    setErrors(newErrors);
    setShowErrors(true);

    return Object.keys(newErrors).length === 0;
  };

  const calculateEndTime = (date: string, time: string, durationMinutes: number): string => {
    if (!date || !time || !durationMinutes) return '';

    try {
      const startDateTime = new Date(`${date}T${time}:00+03:00`);
      const endDateTime = new Date(startDateTime.getTime() + (durationMinutes * 60 * 1000));

      return endDateTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Africa/Nairobi'
      });
    } catch (error) {
      return '';
    }
  };

  const endTime = calculateEndTime(formData.meetingDate, formData.meetingTime, parseInt(formData.duration));

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Convert date format to RFC3339 with East African Time (EAT)
      let scheduledAtRFC3339 = `${formData.meetingDate}T${formData.meetingTime}:00+03:00`;

      // Parse attendee emails
      const attendeeEmails = formData.attendeeEmails.trim()
        ? formData.attendeeEmails.split(',').map(email => email.trim()).filter(email => email)
        : [];

      const meetingData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        scheduledAt: scheduledAtRFC3339,
        duration: parseInt(formData.duration),
        location: formData.location.trim(),
        meetingUrl: formData.meetingUrl.trim(),
        meetingType: formData.meetingType,
        recordingEnabled: formData.meetingType === 'virtual' || formData.meetingType === 'hybrid',
        attendeeEmails: attendeeEmails,
        calendarId: formData.addToCalendar ? 'primary' : null,
      };

      console.log('📤 Sending meeting data:', meetingData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert('Meeting scheduled successfully!');
      // Navigate back to meetings page

    } catch (error) {
      console.error('Failed to create meeting:', error);
      alert('Failed to schedule meeting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ErrorMessage: React.FC<{ error?: string }> = ({ error }) => {
    if (!error || !showErrors) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginTop: spacing.xs,
        paddingLeft: spacing.sm,
        paddingRight: spacing.sm,
      }}>
        <span style={{ color: colors.error, marginRight: spacing.xs }}>⚠️</span>
        <span style={{
          fontSize: typography.fontSize.sm,
          color: colors.error,
          flex: 1,
        }}>
          {error}
        </span>
      </div>
    );
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    backButton: {
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.text,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    formCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    input: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
      marginBottom: spacing.sm,
    },
    inputError: {
      borderColor: colors.error,
    },
    textarea: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
      marginBottom: spacing.sm,
      minHeight: 80,
      resize: 'vertical',
    },
    typeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: spacing.lg,
    },
    typeOption: {
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.backgroundSecondary,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    typeOptionSelected: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    typeHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      marginBottom: spacing.xs,
    },
    typeName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      flex: 1,
    },
    typeDescription: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    durationGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    durationOption: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.backgroundSecondary,
      cursor: 'pointer',
      minWidth: '45%',
      justifyContent: 'space-between',
    },
    durationOptionSelected: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    durationText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
    },
    endTimeContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.sm,
      borderRadius: borderRadius.md,
      backgroundColor: colors.primary + '10',
      border: `1px solid ${colors.primary}`,
      marginTop: spacing.sm,
    },
    endTimeText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
      marginLeft: spacing.xs,
    },
    calendarOption: {
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.backgroundSecondary,
      marginTop: spacing.sm,
      cursor: 'pointer',
    },
    calendarOptionSelected: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    calendarHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      marginBottom: spacing.xs,
    },
    calendarText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      flex: 1,
    },
    calendarDescription: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginLeft: spacing.lg + spacing.sm,
    },
    submitButton: {
      width: '100%',
      padding: spacing.lg,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      marginTop: spacing.xl,
    },
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    generalErrorContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      backgroundColor: colors.error + '10',
      border: `1px solid ${colors.error}`,
      marginBottom: spacing.md,
    },
    generalErrorContent: {
      flex: 1,
      marginLeft: spacing.sm,
    },
    generalErrorTitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.error,
      marginBottom: spacing.xs,
    },
    generalErrorText: {
      fontSize: typography.fontSize.sm,
      color: colors.error,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button
            style={styles.backButton}
            onClick={() => alert('Navigate back to meetings')}
          >
            ←
          </button>
          <h1 style={styles.headerTitle}>Schedule Meeting</h1>
        </div>
      </div>

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
        {/* General error message */}
        {showErrors && Object.keys(errors).length > 0 && (
          <div style={styles.generalErrorContainer}>
            <span style={{ color: colors.error, fontSize: 20 }}>⚠️</span>
            <div style={styles.generalErrorContent}>
              <div style={styles.generalErrorTitle}>
                Please fix the following errors:
              </div>
              <div style={styles.generalErrorText}>
                {Object.keys(errors).length} field{Object.keys(errors).length > 1 ? 's' : ''} need{Object.keys(errors).length === 1 ? 's' : ''} attention
              </div>
            </div>
          </div>
        )}

        <div style={styles.formCard}>
          <h2 style={styles.sectionTitle}>Meeting Details</h2>

          <input
            type="text"
            placeholder="Enter meeting title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            style={{
              ...styles.input,
              ...(errors.title && showErrors ? styles.inputError : {}),
            }}
          />
          <ErrorMessage error={errors.title} />

          <textarea
            placeholder="Meeting agenda and details..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            style={{
              ...styles.textarea,
              ...(errors.description && showErrors ? styles.inputError : {}),
            }}
            rows={3}
          />
          <ErrorMessage error={errors.description} />

          <input
            type="date"
            value={formData.meetingDate}
            onChange={(e) => handleInputChange('meetingDate', e.target.value)}
            style={{
              ...styles.input,
              ...(errors.meetingDate && showErrors ? styles.inputError : {}),
            }}
          />
          <ErrorMessage error={errors.meetingDate} />

          <input
            type="time"
            value={formData.meetingTime}
            onChange={(e) => handleInputChange('meetingTime', e.target.value)}
            style={{
              ...styles.input,
              ...(errors.meetingTime && showErrors ? styles.inputError : {}),
            }}
          />
          <ErrorMessage error={errors.meetingTime} />

          {endTime && (
            <div style={styles.endTimeContainer}>
              <span>🕐</span>
              <span style={styles.endTimeText}>
                Meeting will end at: {endTime} EAT
              </span>
            </div>
          )}
        </div>

        <div style={styles.formCard}>
          <h2 style={styles.sectionTitle}>Meeting Type</h2>

          <div style={styles.typeGrid}>
            {meetingTypes.map((type) => (
              <div
                key={type.id}
                style={{
                  ...styles.typeOption,
                  ...(formData.meetingType === type.id ? styles.typeOptionSelected : {}),
                }}
                onClick={() => handleInputChange('meetingType', type.id)}
              >
                <div style={styles.typeHeader}>
                  <span style={{ fontSize: 24 }}>
                    {type.icon === 'location' ? '📍' :
                     type.icon === 'videocam' ? '📹' :
                     type.icon === 'people' ? '👥' : '📅'}
                  </span>
                  <span style={styles.typeName}>{type.name}</span>
                  {formData.meetingType === type.id && (
                    <span style={{ color: colors.primary }}>✓</span>
                  )}
                </div>
                <p style={styles.typeDescription}>{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.formCard}>
          <h2 style={styles.sectionTitle}>Duration</h2>

          <div style={styles.durationGrid}>
            {durations.map((duration) => (
              <div
                key={duration.value}
                style={{
                  ...styles.durationOption,
                  ...(formData.duration === duration.value ? styles.durationOptionSelected : {}),
                }}
                onClick={() => handleInputChange('duration', duration.value)}
              >
                <span style={styles.durationText}>{duration.label}</span>
                {formData.duration === duration.value && (
                  <span style={{ color: colors.primary }}>✓</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {(formData.meetingType === 'physical' || formData.meetingType === 'hybrid') && (
          <div style={styles.formCard}>
            <h2 style={styles.sectionTitle}>Location Details</h2>

            <input
              type="text"
              placeholder="Enter meeting venue/address"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              style={{
                ...styles.input,
                ...(errors.location && showErrors ? styles.inputError : {}),
              }}
            />
            <ErrorMessage error={errors.location} />
          </div>
        )}

        {(formData.meetingType === 'virtual' || formData.meetingType === 'hybrid') && (
          <div style={styles.formCard}>
            <h2 style={styles.sectionTitle}>Virtual Meeting Details</h2>

            <input
              type="url"
              placeholder="Zoom, Teams, or other meeting link"
              value={formData.meetingUrl}
              onChange={(e) => handleInputChange('meetingUrl', e.target.value)}
              style={{
                ...styles.input,
                ...(errors.meetingUrl && showErrors ? styles.inputError : {}),
              }}
            />
            <ErrorMessage error={errors.meetingUrl} />
          </div>
        )}

        <div style={styles.formCard}>
          <h2 style={styles.sectionTitle}>Attendees & Calendar Integration</h2>

          <textarea
            placeholder="email1@example.com, email2@example.com"
            value={formData.attendeeEmails}
            onChange={(e) => handleInputChange('attendeeEmails', e.target.value)}
            style={{
              ...styles.textarea,
              ...(errors.attendeeEmails && showErrors ? styles.inputError : {}),
            }}
            rows={2}
          />
          <ErrorMessage error={errors.attendeeEmails} />

          <div
            style={{
              ...styles.calendarOption,
              ...(formData.addToCalendar ? styles.calendarOptionSelected : {}),
            }}
            onClick={() => handleInputChange('addToCalendar', !formData.addToCalendar)}
          >
            <div style={styles.calendarHeader}>
              <span style={{ fontSize: 24 }}>📅</span>
              <span style={styles.calendarText}>Add to Google Calendar</span>
              <span style={{
                color: formData.addToCalendar ? colors.primary : colors.textSecondary
              }}>
                {formData.addToCalendar ? '✓' : '○'}
              </span>
            </div>
            <p style={styles.calendarDescription}>
              Automatically create calendar event and send invites to attendees
            </p>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            ...styles.submitButton,
            ...(loading ? styles.submitButtonDisabled : {}),
          }}
        >
          {loading ? (
            <>
              <span>⏳</span>
              Scheduling Meeting...
            </>
          ) : (
            <>
              <span>📅</span>
              Schedule Meeting
            </>
          )}
        </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMeeting;